// Library Methods
import { useCallback } from "react";

// Constants
import { gridTypeEnums } from "src/constants/enums";

type HTMLStyleType = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

const axes = ["YNeg", "YPos", "XNeg", "XPos"];

const gridDic = {
  [gridTypeEnums.G_10_2.type]: "10",
  [gridTypeEnums.G_24_2.type]: "30",
  [gridTypeEnums.G_24_2_C.type]: "30",
  [gridTypeEnums.G_30_2.type]: "30",
  [gridTypeEnums.G_120.type]: "80",
  [gridTypeEnums.FULL_120.type]: "60",
  [gridTypeEnums.G_C_20.type]: "30",
  [gridTypeEnums.G_C_40.type]: "30",
};

const ChartCross = ({
  height = 312,
  width = 312,
  borderWidth = 2,
  tickSize = 6,
  tickNum = 3,
  gridType = gridTypeEnums.G_10_2.type,
}) => {
  const widthPx = `${width}px`;
  const heightPx = `${height}px`;

  const halfWidthPx = `${(width - borderWidth) / 2}px`;
  const halfHeightPx = `${(height - borderWidth) / 2}px`;

  const tickSizePx = `${tickSize}px`;
  const borderWidthPx = `${borderWidth}px`;

  const getChartTicks = useCallback(
    (axis: string) => {
      return [...Array(tickNum)].map((_, index) => {
        const styles = {
          YNeg: {
            position: "absolute",
            borderBottom: `${borderWidthPx} solid #000`,
            width: tickSizePx,
            left: halfWidthPx,
            transform: "translateX(-50%)",

            top: `calc(
              ${halfHeightPx} + ${halfHeightPx} / ${tickNum} * ${index + 1}
            )`,
          } as HTMLStyleType,
          YPos: {
            position: "absolute",
            borderBottom: `${borderWidthPx} solid #000`,
            width: tickSizePx,
            left: halfWidthPx,
            transform: "translateX(-50%)",

            top: `calc(
              ${halfHeightPx} - ${halfHeightPx} / ${tickNum} * ${index + 1}
            )`,
          } as HTMLStyleType,
          XNeg: {
            position: "absolute",
            borderRight: `${borderWidthPx} solid #000`,
            height: tickSizePx,
            top: halfHeightPx,
            transform: "translateY(-50%)",

            left: `calc(
              ${halfWidthPx} - ${halfWidthPx} / ${tickNum} * ${index + 1}
            )`,
          } as HTMLStyleType,
          XPos: {
            position: "absolute",
            borderRight: `${borderWidthPx} solid #000`,
            height: tickSizePx,
            top: halfHeightPx,
            transform: "translateY(-50%)",

            left: `calc(
              ${halfWidthPx} + ${halfWidthPx} / ${tickNum} * ${index + 1}
            )`,
          } as HTMLStyleType,
        };

        return (
          <div
            key={`${axis}${index + 1}`}
            style={{ ...styles[axis as keyof typeof styles] }}
          />
        );
      });
    },
    [borderWidthPx, halfWidthPx, halfHeightPx, tickNum, tickSizePx]
  );

  return (
    <div>
      {/* Leftmost label on X axis */}
      <div
        style={{
          fontSize: "12px",
          position: "absolute",
          marginTop: `calc(${halfHeightPx} - 10px)`,
          left: "-24px",
          zIndex: 10,
        }}
      >
        {gridDic[gridType]}
      </div>

      {/* Rightmost label on X axis */}
      <div
        style={{
          fontSize: "12px",
          position: "absolute",
          marginTop: `calc(${halfHeightPx} - 10px)`,
          left: `calc(${widthPx} + 5px)`,
          zIndex: 10,
        }}
      >
        {gridDic[gridType]}
      </div>

      {/* Y axis line */}
      <div
        style={{
          position: "absolute",
          borderRight: `${borderWidthPx} solid #000`,
          height: heightPx,
          marginLeft: halfWidthPx,
        }}
      />

      {/* X axis line */}
      <div
        style={{
          position: "absolute",
          borderBottom: `${borderWidthPx} solid #000`,
          width: widthPx,
          marginTop: halfHeightPx,
        }}
      />

      {/* Tick marks on X and Y axes */}
      {axes.map((axis: string) => getChartTicks(axis))}
    </div>
  );
};

export default ChartCross;
